"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

const RE_SPLIT_WORDS = /\s+/;
const RE_NON_ALPHA = /[^a-z\s'-]/g;

const STOP_WORDS = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "but",
  "in",
  "on",
  "at",
  "to",
  "for",
  "of",
  "with",
  "by",
  "from",
  "is",
  "it",
  "this",
  "that",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "have",
  "has",
  "had",
  "do",
  "does",
  "did",
  "will",
  "would",
  "could",
  "should",
  "may",
  "might",
  "shall",
  "can",
  "not",
  "no",
  "so",
  "if",
  "as",
  "its",
  "my",
  "your",
  "his",
  "her",
  "our",
  "their",
  "we",
  "you",
  "he",
  "she",
  "they",
  "me",
  "him",
  "us",
  "them",
  "i",
  "am",
  "what",
  "which",
  "who",
  "whom",
  "how",
  "when",
  "where",
  "why",
  "all",
  "each",
  "every",
  "both",
  "few",
  "more",
  "most",
  "other",
  "some",
  "such",
  "than",
  "too",
  "very",
  "just",
  "about",
]);

interface DensityItem {
  text: string;
  count: number;
  density: number;
}

function getWords(text: string): string[] {
  return text
    .toLowerCase()
    .replace(RE_NON_ALPHA, " ")
    .split(RE_SPLIT_WORDS)
    .filter((w) => w.length > 0);
}

function getSingleWordDensity(words: string[], limit: number): DensityItem[] {
  const total = words.length;
  if (total === 0) {
    return [];
  }

  const freq: Record<string, number> = {};
  for (const w of words) {
    if (w.length > 1 && !STOP_WORDS.has(w)) {
      freq[w] = (freq[w] || 0) + 1;
    }
  }

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([text, count]) => ({
      text,
      count,
      density: (count / total) * 100,
    }));
}

function getNgramDensity(
  words: string[],
  n: number,
  limit: number
): DensityItem[] {
  const total = words.length;
  if (total < n) {
    return [];
  }

  const freq: Record<string, number> = {};
  for (let i = 0; i <= words.length - n; i++) {
    const gram = words.slice(i, i + n).join(" ");
    freq[gram] = (freq[gram] || 0) + 1;
  }

  return Object.entries(freq)
    .filter(([_, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([text, count]) => ({
      text,
      count,
      density: (count / total) * 100,
    }));
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function getDensityColor(density: number): string {
  if (density > 3) {
    return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
  }
  if (density > 1.5) {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
  }
  return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
}

function DensityTable({
  title,
  items,
}: {
  title: string;
  items: DensityItem[];
}) {
  const fmt = useToolNumber();
  const strings = useToolUi(WORD_DENSITY_ANALYZER_STRINGS);

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <h2 className="font-semibold text-foreground text-lg">{title}</h2>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-border border-b bg-muted/50">
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                {strings.columnWordOrPhrase}
              </th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                {strings.columnCount}
              </th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                {strings.columnDensity}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                className="border-border border-b last:border-0"
                key={item.text}
              >
                <td className="px-4 py-2.5 text-foreground">{item.text}</td>
                <td className="px-4 py-2.5 text-right text-muted-foreground">
                  {fmt(item.count)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 font-medium text-xs ${getDensityColor(item.density)}`}
                  >
                    {fmt(item.density, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    %
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type TabOption = "single" | "bigram" | "trigram";

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_DENSITY_ANALYZER_STRINGS = {
  textToAnalyzeKeywordDensity: "Text to analyze keyword density",
  pasteOrTypeYourText:
    "Paste or type your text here to analyze keyword density...",
  totalWords: "Total Words",
  uniqueWords: "Unique Words",
  vocabularyRichness: "Vocabulary Richness",
  repeatedPhrases: "Repeated Phrases",
  noSignificantKeywordsFoundTry:
    "No significant keywords found. Try adding more content.",
  noRepeatedPhrasesFoundPhrases:
    "No repeated phrases found. Phrases must appear at least twice to be listed.",
  densityGuide: "Density Guide",
  naturalUsage: "Natural usage",
  targetedKeyword: "Targeted keyword",
  potentiallyOverused: "Potentially overused",
  columnWordOrPhrase: "Word / Phrase",
  columnCount: "Count",
  columnDensity: "Density",
  tabSingleWords: "Single Words",
  tabTwoWordPhrases: "Two-Word Phrases",
  tabThreeWordPhrases: "Three-Word Phrases",
  titleTopSingleWords: "Top Single Words",
  titleTopTwoWordPhrases: "Top Two-Word Phrases",
  titleTopThreeWordPhrases: "Top Three-Word Phrases",
} as const;

export default function WordDensityAnalyzerWidget() {
  const [text, setText] = useState("");
  const [activeTab, setActiveTab] = useState<TabOption>("single");

  const strings = useToolUi(WORD_DENSITY_ANALYZER_STRINGS);
  const hasText = text.trim().length > 0;
  const words = hasText ? getWords(text) : [];
  const totalWords = words.length;
  const uniqueWords = new Set(words).size;

  const singleDensity = hasText ? getSingleWordDensity(words, 20) : [];
  const bigramDensity = hasText ? getNgramDensity(words, 2, 15) : [];
  const trigramDensity = hasText ? getNgramDensity(words, 3, 15) : [];

  const tabs: { value: TabOption; label: string }[] = [
    { value: "single", label: strings.tabSingleWords },
    { value: "bigram", label: strings.tabTwoWordPhrases },
    { value: "trigram", label: strings.tabThreeWordPhrases },
  ];

  function getActiveItems(): DensityItem[] {
    switch (activeTab) {
      case "single":
        return singleDensity;
      case "bigram":
        return bigramDensity;
      case "trigram":
        return trigramDensity;
      default:
        return singleDensity;
    }
  }

  function getActiveTitle(): string {
    switch (activeTab) {
      case "single":
        return strings.titleTopSingleWords;
      case "bigram":
        return strings.titleTopTwoWordPhrases;
      case "trigram":
        return strings.titleTopThreeWordPhrases;
      default:
        return strings.titleTopSingleWords;
    }
  }

  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textToAnalyzeKeywordDensity}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      {/* Stats */}
      {hasText && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label={strings.totalWords} value={fmt(totalWords)} />
            <StatCard label={strings.uniqueWords} value={fmt(uniqueWords)} />
            <StatCard
              label={strings.vocabularyRichness}
              value={
                totalWords > 0
                  ? `${fmt((uniqueWords / totalWords) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
                  : "0%"
              }
            />
            <StatCard
              label={strings.repeatedPhrases}
              value={bigramDensity.length + trigramDensity.length}
            />
          </div>

          {/* Tab navigation */}
          <div className="flex flex-wrap gap-2">
            {tabs.map(({ value, label }) => (
              <button
                className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
                  activeTab === value
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-foreground hover:border-foreground/20 hover:bg-accent"
                }`}
                key={value}
                onClick={() => setActiveTab(value)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>

          {/* Density table for active tab */}
          <DensityTable items={getActiveItems()} title={getActiveTitle()} />

          {getActiveItems().length === 0 && (
            <div className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm">
              {activeTab === "single"
                ? strings.noSignificantKeywordsFoundTry
                : strings.noRepeatedPhrasesFoundPhrases}
            </div>
          )}

          {/* Density guide */}
          <section className="space-y-3">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.densityGuide}
            </h2>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-border bg-card p-3 text-center">
                <span className="inline-block rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-800 text-xs dark:bg-green-900 dark:text-green-200">
                  0-1.5%
                </span>
                <p className="mt-1 text-muted-foreground text-xs">
                  {strings.naturalUsage}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-3 text-center">
                <span className="inline-block rounded bg-yellow-100 px-1.5 py-0.5 font-medium text-xs text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  1.5-3%
                </span>
                <p className="mt-1 text-muted-foreground text-xs">
                  {strings.targetedKeyword}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-3 text-center">
                <span className="inline-block rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-800 text-xs dark:bg-red-900 dark:text-red-200">
                  3%+
                </span>
                <p className="mt-1 text-muted-foreground text-xs">
                  {strings.potentiallyOverused}
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
